import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '../tokens/cn';
import { focusRing, transitionBase } from '../tokens/variants';

export const RadioGroup = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Root>,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn('grid gap-2', className)}
    {...props}
  />
));
RadioGroup.displayName = 'RadioGroup';

export interface RadioGroupItemProps
  extends ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  size?: 'sm' | 'md' | 'lg';
}

const sizeMap = {
  sm: { box: 'h-4 w-4', dot: 'h-1.5 w-1.5' },
  md: { box: 'h-5 w-5', dot: 'h-2 w-2' },
  lg: { box: 'h-6 w-6', dot: 'h-2.5 w-2.5' },
};

export const RadioGroupItem = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, size = 'md', ...props }, ref) => {
  const { box, dot } = sizeMap[size];
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'shrink-0 rounded-full border border-[var(--tk-surface-border-strong)]',
        'bg-[var(--tk-surface-bg-elevated)]',
        'data-[state=checked]:border-[var(--tk-brand-bg)]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        focusRing,
        transitionBase,
        box,
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <span className={cn('rounded-full bg-[var(--tk-brand-bg)]', dot)} />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = 'RadioGroupItem';
